import React from 'react';
import { motion } from 'framer-motion';

const benchmarkData = [
  { name: 'My Portfolio', value: '₹63L', color: 'wealth-green', diff: null, isBase: true },
  { name: 'Nifty 50', value: '₹58.76L', color: 'tech-blue', diff: '+₹4.2L', isPositive: true, note: 'Consistent outperformance' },
  { name: 'Nifty 500', value: '₹60.80L', color: 'tech-blue', diff: '+₹2.19L', isPositive: true, note: 'Consistent outperformance' },
  { name: 'Nifty Next 50', value: '₹63.83L', color: 'missed-gold', diff: '-₹0.83L', isPositive: false, note: 'High volatility in Next 50' },
  { name: 'Smallcap 250', value: '₹61.47L', color: 'tech-blue', diff: '+₹1.52L', isPositive: true, note: 'Outperformed due to recent fall' },
  { name: 'Midcap 150', value: '₹66.27L', color: 'alert-red', diff: '-₹3.27L', isPositive: false, note: 'Underperforming' },
  { name: 'GOLD', value: '₹98.78L', color: 'missed-gold', diff: '-₹35.7L', isPositive: false, note: '2025 gold rally', isShocking: true },
];

const TheDiscovery = () => {
  return (
    <section className="relative py-24 lg:py-32 bg-deep-space overflow-hidden" data-testid="the-discovery">
      {/* Background Image with Dark Overlay */}
      <div 
        className="absolute inset-0 opacity-[0.08]"
        style={{
          backgroundImage: 'url(https://images.unsplash.com/photo-1673904788072-40f58f0c43e1?crop=entropy&cs=srgb&fm=jpg&q=85)',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          filter: 'saturate(0.5)',
        }}
      />
      
      {/* Gold Glow */}
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] bg-missed-gold/10 rounded-full blur-[200px]" />
      
      <div className="relative max-w-7xl mx-auto px-6 lg:px-8">
        {/* Shocking Headline */}
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="mb-16"
        >
          <h2 className="font-outfit text-3xl lg:text-5xl xl:text-6xl font-extrabold tracking-tighter text-white leading-tight">
            So I Did What Any Excel-Based<br />
            Investor Would Do
          </h2>
          <p className="mt-6 text-xl lg:text-2xl text-slate-400 font-ibm-plex max-w-3xl">
            Built a tool to automate the comparison. Mimicked every SIP, every transaction date, 
            every amount. Then simulated: <span className="text-white font-semibold">"What if all this money went into Nifty 50? Or Gold?"</span>
          </p>
        </motion.div>
        
        {/* Comparison Grid */}
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8, delay: 0.2 }}
          className="mb-16"
        >
          <div className="bg-white/5 backdrop-blur-2xl border border-white/10 rounded-2xl p-6 lg:p-10 shadow-[0_8px_32px_rgba(0,0,0,0.3)]">
            <h3 className="font-outfit text-xl lg:text-2xl font-bold text-white mb-8">
              My ₹53L investment would have grown to:
            </h3>
            
            <div className="grid gap-4">
              {benchmarkData.map((item, index) => (
                <motion.div
                  key={item.name}
                  initial={{ opacity: 0, x: -20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.4, delay: index * 0.1 }}
                  className={`
                    relative flex flex-col sm:flex-row sm:items-center justify-between gap-4 p-4 lg:p-6 rounded-xl
                    ${item.isBase ? 'bg-wealth-green/10 border-2 border-wealth-green/30' : 
                      item.isShocking ? 'bg-missed-gold/10 border-2 border-missed-gold/30' : 
                      'bg-white/5 border border-white/10'}
                  `}
                >
                  {/* Left: Name */}
                  <div className="flex-1">
                    <div className="font-ibm-plex text-lg lg:text-xl text-white font-medium">
                      {item.name}
                      {item.isBase && <span className="ml-2 text-xs text-wealth-green bg-wealth-green/20 px-2 py-1 rounded-full">ACTUAL</span>}
                      {item.isShocking && <span className="ml-2 text-xs text-missed-gold bg-missed-gold/20 px-2 py-1 rounded-full">SHOCKING</span>}
                    </div>
                    {item.note && <div className="text-sm text-slate-400 mt-1">{item.note}</div>}
                  </div>
                  
                  {/* Center: Value */}
                  <div className={`font-jetbrains text-2xl lg:text-3xl font-bold ${
                    item.isBase ? 'text-wealth-green' : 
                    item.isShocking ? 'text-missed-gold' : 'text-white'
                  }`}>
                    {item.value}
                  </div>
                  
                  {/* Right: Difference */}
                  {item.diff && (
                    <div className={`font-jetbrains text-lg lg:text-xl font-bold ${
                      item.isPositive ? 'text-wealth-green' : 'text-alert-red'
                    }`}>
                      {item.diff}
                      {item.isPositive ? ' ✓' : ''}
                    </div>
                  )}
                </motion.div>
              ))}
            </div>
          </div>
        </motion.div>
        
        {/* The Gold Revelation */}
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          whileInView={{ opacity: 1, scale: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8, delay: 0.4 }}
          className="text-center"
        >
          <div className="inline-block bg-gradient-to-r from-missed-gold/20 to-yellow-500/10 backdrop-blur-xl border-2 border-missed-gold/40 rounded-2xl p-8 lg:p-12">
            {/* Glow effect */}
            <div className="absolute inset-0 bg-missed-gold/10 rounded-2xl blur-3xl -z-10" />
            
            <p className="font-ibm-plex text-xl text-slate-300 mb-4">Let Me Say That Again:</p>
            
            <div className="font-jetbrains text-5xl lg:text-7xl xl:text-8xl font-extrabold text-missed-gold mb-4">
              ₹98.78 LAKHS
            </div>
            
            <p className="font-outfit text-xl lg:text-2xl text-white font-semibold mb-4">
              If I had just put everything in Gold
            </p>
            
            <div className="inline-flex items-center gap-3 bg-alert-red/20 border border-alert-red/30 rounded-full px-6 py-3">
              <span className="font-jetbrains text-2xl lg:text-3xl font-bold text-alert-red">₹35L</span>
              <span className="text-white font-ibm-plex">less than what I could have had</span>
            </div>
            
            <p className="mt-8 text-slate-400 font-ibm-plex max-w-2xl mx-auto">
              My entire "diversified equity portfolio" underperformed Gold by 56.8% because of the 2025 Gold rally.
              This hit me hard - why asset allocation is important.
            </p>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default TheDiscovery;
